import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { GetHelper, payloadsEqual } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';

export type PersonConditionsValues = {
  investor_condition: string;
  signed: boolean;
  qualification?: string;
  id?: number;
};

@Component({
  selector: 'app-signup-unified-person-conditions',
  templateUrl: './signup-unified-person-conditions.component.html',
  styleUrls: ['./signup-unified-person-conditions.component.scss'],
})
export class SignupUnifiedPersonConditionsComponent
  implements OnInit, OnDestroy
{
  form = this.formBuilder.group({
    qualification: [null, Validators.required],
    investor_condition: [null, [Validators.required]],
    signed: [false, [Validators.requiredTrue]],
  });

  data: PersonConditionsValues;
  fullScreenLoading = false;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, any> | null = null;

  qualificationOptions = [
    { label: 'Certificado', value: 'certified' },
    { label: 'Qualificado', value: 'qualified' },
    { label: 'Profissional', value: 'professional' },
  ];

  options = [
    {
      value: 'never',
      label:
        'Declaro, sob as penas da lei, que possuo investimentos financeiros em valor superior a R$ 10.000.000,00 (dez milhões de reais)',
    },
    {
      value: 'up_to_five',
      label:
        'Declaro, sob as penas da lei, que possuo investimentos financeiros em valor superior a R$ 1.000.000,00 (um milhão de reais)',
    },
    {
      value: 'up_to_fifteen',
      label:
        'Declaro que tenho sido aprovado em exames de qualificação técnica ou possuo certificações aprovadas pela CVM como requisitos para o registro de agentes autônomos de investimento, administradores de carteira, analistas e consultores de valores mobiliários, em relação a seus recursos próprios',
    },
  ];

  texts = [
    {
      showIn: ['never'],
      text: 'Ao assinar este termo, afirmo minha condição de investidor profissional e declaro possuir conhecimento sobre o mercado financeiro suficiente para que não me sejam aplicáveis um conjunto de proteções legais e regulamentares conferidas aos demais investidores.<br><br>Como investidor profissional, atesto ser capaz de entender e ponderar os riscos financeiros relacionados à aplicação de meus recursos em valores mobiliários que só podem ser adquiridos por investidores profissionais.',
    },
    {
      showIn: ['up_to_five'],
      text: `Ao assinar este termo, afirmo minha condição de investidor qualificado e declaro possuir conhecimento sobre o mercado financeiro suficiente para que não me sejam aplicáveis um conjunto de proteções legais e regulamentares conferidas aos investidores que não sejam qualificados.<br><br>Como investidor qualificado, atesto ser capaz de entender e ponderar os riscos financeiros relacionados à aplicação de meus recursos em valores mobiliários que só podem ser adquiridos por investidores qualificados.`,
    },
    {
      showIn: ['up_to_fifteen'],
      text: `Ao assinar este termo, afirmo minha condição de investidor certificado e declaro possuir conhecimento sobre o mercado financeiro suficiente para que não me sejam aplicáveis um conjunto de proteções legais e regulamentares conferidas aos investidores que não sejam certificados.<br><br>Como investidor certificado, atesto ser capaz de entender e ponderar os riscos financeiros relacionados à aplicação de meus recursos em valores mobiliários que só podem ser adquiridos por investidores certificados.`,
    },
  ];
  event: any;

  constructor(
    private formBuilder: FormBuilder,
    private signupService: SignupService,
    private api: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    this.event = this.signupService.sendPersonData.subscribe((value) => {
      if (value === 'conditions') {
        this.sendData();
      }
    });

    this.getData();

    this.form.statusChanges.subscribe((status) => {
      this.signupService.setPersonFormStatus('conditions', status === 'VALID');
    });
  }

  getText() {
    const { investor_condition } = this.form.value;

    const text = this.texts.find((text) =>
      text.showIn.includes(investor_condition)
    );

    return text?.text;
  }

  async getData() {
    try {
      this.fullScreenLoading = true; // Ativar spinner de tela cheia

      // Sempre pegar o UUID da URL
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        this.fullScreenLoading = false;
        return;
      }

      const { data } = await this.api.get({
        route: 'api/registration/investor-profile-pf/',
        params: { register_uuid: routeId },
        token: true,
      });

      const normalizedData = Array.isArray(data) ? data[0] : data;
      this.data = normalizedData ?? null;

      // Validar se qualification está nas opções disponíveis
      let qualificationValue = this.data?.qualification;
      if (qualificationValue) {
        const isValidQualification = this.qualificationOptions.some(
          (option) => option.value === qualificationValue
        );
        if (!isValidQualification) {
          qualificationValue = null;
        }
      }

      this.form.patchValue({
        qualification: qualificationValue,
        investor_condition: this.data?.investor_condition,
        signed: this.data?.investor_condition ? true : false,
      });
      this.form.updateValueAndValidity();
      this.initialPayload = this.data != null ? this.buildPayloadFromForm() : null;
    } catch (error) {
      this.initialPayload = null;
    } finally {
      this.fullScreenLoading = false; // Desativar spinner de tela cheia
    }
  }

  private buildPayloadFromForm(): Record<string, any> {
    const valuesHelper = new GetHelper(this.form.value);
    return {
      qualification: valuesHelper.get('qualification'),
      investor_condition: valuesHelper.get('investor_condition'),
      signed: valuesHelper.get('signed'),
      is_exempt: false,
    };
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      this.toast.show(
        'error',
        'Erro',
        'UUID do registro não encontrado na URL'
      );
      return;
    }

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      this.fullScreenLoading = false;
      await this.signupService.updateUserStepPF('register', 'tax', routeId);
      return;
    }

    this.signupService.loading = true;
    this.fullScreenLoading = true;

    try {
      if (this.data && this.data.id) {
        await this.api.put({
          route: `api/registration/investor-profile-pf/${this.data.id}/`,
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });
      } else {
        await this.api.post({
          route: 'api/registration/investor-profile-pf/',
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });
      }

      this.initialPayload = this.buildPayloadFromForm();
      await this.signupService.updateUserStepPF('register', 'tax', routeId);
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao salvar os dados'
      );
      this.signupService.loading = false;
      this.fullScreenLoading = false;
    } finally {
      this.signupService.loading = false;
      this.fullScreenLoading = false;
    }
  }
}
